/**
 * Presentational layer for the focused DrillView. Renders the whole
 * `SpotCard` (table / simplified — it owns the layout preference, the
 * action bar, and the report flow) and, alongside it, a per-spot
 * CONVERSATION rail: the user's action, the coach's verdict, and any
 * "walk through the reasoning" / free-form follow-ups stack there. The
 * conversation continues IN this rail — it never navigates to /chat.
 *
 * Two-column on desktop (`md:` and up), stacked on mobile. The frame is
 * pure props — DrillView owns all the state + the stream. Coach prose
 * renders through the shared `VerdictProse` so the focused view and the
 * conversational view never drift.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { SpotCard } from "@/features/chat/components/SpotCard";
import { SpotSkeleton } from "@/features/drills/components/SpotSkeleton";
import { VerdictProse } from "@/features/chat/components/VerdictProse";
import { ChevronRight, ICON_STROKE, Zap } from "@/shared/components/icons";
import { LogoMark } from "@/shared/components/LogoMark";
import { dailyChallengeRoute } from "@/shared/chatRoute";
import {
  DIFFICULTY_LABEL,
  nextIncompleteDifficulty,
  type DailyChallenge,
} from "@/features/daily/lib/dailyChallenge";
import { nextPhaseFromStream } from "@/features/drills/lib/drillStream";
import type {
  DrillMessage,
  DrillPhase,
} from "@/features/drills/components/DrillView";
import type { DrillDifficulty, SpotAction, SpotPayload } from "@/shared/models";

interface DrillFrameProps {
  phase: DrillPhase;
  spot?: SpotPayload;
  conversationId?: string;
  /** The current spot's conversation — user action, verdict, follow-ups. */
  transcript: DrillMessage[];
  /** Live token buffer while a reply streams. */
  streamBuffer: string;
  errorMessage: string | null;
  /** XP earned by the just-graded turn (0 = no tick). */
  xpDelta: number;
  isDaily: boolean;
  dailyChallenge?: DailyChallenge;
  onAction: (action: SpotAction) => void;
  onNextSpot: () => void;
  onWalkThrough: () => void;
  onAsk: (text: string) => void;
  onRetry: () => void;
  onSheetLink: (sheetId: string) => void;
}

export function DrillFrame({
  phase,
  spot,
  conversationId,
  transcript,
  streamBuffer,
  errorMessage,
  xpDelta,
  isDaily,
  dailyChallenge,
  onAction,
  onNextSpot,
  onWalkThrough,
  onAsk,
  onRetry,
  onSheetLink,
}: DrillFrameProps) {
  // Only awaiting-action lets the user act; every other phase freezes the
  // spot's buttons.
  const spotLocked = phase !== "awaiting-action";

  if (phase === "error") {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center space-y-4 max-w-sm">
          <p className="text-sm text-text-muted">
            {errorMessage ?? "Couldn't load this drill."}
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="px-4 py-2.5 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent/90 transition"
          >
            Try another spot
          </button>
        </div>
      </div>
    );
  }

  // Show the dealing skeleton before the first spot AND while dealing the
  // next one — keyed off `phase` (not by clearing `currentSpot`) so the old
  // spot stays available for cap/error recovery. Layout-stable, no shift.
  const showSpot = spot && phase !== "loading-spot";

  return (
    // Desktop: the frame fills the viewport and each column scrolls
    // independently (so a long conversation scrolls the rail, not the whole
    // page). Mobile: stacked + the whole frame scrolls.
    <div className="flex-1 min-h-0 overflow-y-auto md:overflow-hidden scroll-momentum">
      <div className="mx-auto max-w-6xl px-4 md:px-8 grid gap-6 grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(300px,380px)] md:h-full">
        {/* Spot — full SpotCard, or the dealing skeleton. Own scroll on
            desktop so a tall spot doesn't push the rail. */}
        <div className="min-w-0 py-6 md:min-h-0 md:overflow-y-auto">
          <div className="rounded-2xl border border-border/70 bg-surface">
            {showSpot ? (
              <SpotCard
                spot={spot}
                locked={spotLocked}
                onAction={onAction}
                conversationId={conversationId}
                onNextSpot={onNextSpot}
              />
            ) : (
              <SpotSkeleton />
            )}
          </div>
        </div>

        {/* Conversation rail — its own bounded height on desktop; the thread
            inside scrolls while the footer stays pinned. */}
        <div className="min-w-0 pb-6 md:py-6 md:min-h-0 md:h-full">
          {spot ? (
            <ConversationRail
              phase={phase}
              transcript={transcript}
              streamBuffer={streamBuffer}
              xpDelta={xpDelta}
              isDaily={isDaily}
              dailyChallenge={dailyChallenge}
              onNextSpot={onNextSpot}
              onWalkThrough={onWalkThrough}
              onAsk={onAsk}
              onSheetLink={onSheetLink}
            />
          ) : (
            <div className="rounded-2xl border border-dashed border-border/70 bg-surface/40 p-5">
              <div className="h-3.5 w-4/5 rounded-full bg-text/5 animate-pulse" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ConversationRail({
  phase,
  transcript,
  streamBuffer,
  xpDelta,
  isDaily,
  dailyChallenge,
  onNextSpot,
  onWalkThrough,
  onAsk,
  onSheetLink,
}: {
  phase: DrillPhase;
  transcript: DrillMessage[];
  streamBuffer: string;
  xpDelta: number;
  isDaily: boolean;
  dailyChallenge?: DailyChallenge;
  onNextSpot: () => void;
  onWalkThrough: () => void;
  onAsk: (text: string) => void;
  onSheetLink: (id: string) => void;
}) {
  const endRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [transcript, streamBuffer]);

  if (phase === "awaiting-action") {
    return (
      <div className="rounded-2xl border border-dashed border-border/70 bg-surface/40 p-5 text-sm text-text-muted">
        Make your decision — the coach grades it and explains the read.
      </div>
    );
  }

  // Dealing the next spot. The coach often streams a one-line intro before
  // the spot block; suppress it (it would flash then vanish) — show a quiet
  // loading state instead.
  if (phase === "loading-spot") {
    return (
      <div className="rounded-2xl border border-border/70 bg-surface p-5">
        <LoadingPulse label="Dealing your spot…" />
      </div>
    );
  }

  const streaming = phase === "streaming";
  // Strip any partial structured block from the live buffer so JSON never
  // flashes mid-stream.
  const live = streamBuffer ? nextPhaseFromStream(streamBuffer) : null;
  const streamingProse = live && live.kind === "verdict" ? live.text : "";

  return (
    <div className="rounded-2xl border border-border/70 bg-surface flex flex-col md:h-full overflow-hidden">
      {/* Thread — scrolls on desktop; the page scrolls on mobile. */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5 space-y-3.5">
        {transcript.map((m, i) =>
          m.role === "user" ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[88%] rounded-2xl bg-accent/20 text-text px-3.5 py-2 text-sm">
                {m.content}
              </div>
            </div>
          ) : (
            <div key={i} className="prose-chat text-sm text-text leading-relaxed">
              <VerdictProse text={m.content} onSheetLink={onSheetLink} />
            </div>
          ),
        )}

        {streaming &&
          (streamingProse ? (
            <div className="prose-chat text-sm text-text leading-relaxed">
              <VerdictProse text={streamingProse} onSheetLink={onSheetLink} />
            </div>
          ) : (
            <LoadingPulse label="Reading your decision…" />
          ))}

        <div ref={endRef} />
      </div>

      {/* Footer — pinned below the scrolling thread. */}
      {phase === "ready" && (
        <div className="flex-none border-t border-border/40 p-4 sm:p-5 space-y-2">
          {xpDelta > 0 && <XpEarnedTick amount={xpDelta} />}
          <FollowUpInput onAsk={onAsk} />
          <RailCtas
            isDaily={isDaily}
            dailyChallenge={dailyChallenge}
            onNextSpot={onNextSpot}
            onWalkThrough={onWalkThrough}
          />
        </div>
      )}
    </div>
  );
}

function FollowUpInput({ onAsk }: { onAsk: (text: string) => void }) {
  const [draft, setDraft] = useState("");
  function submit() {
    const t = draft.trim();
    if (!t) return;
    setDraft("");
    onAsk(t);
  }
  return (
    <div className="flex gap-2 items-end pt-1">
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="Ask a follow-up…"
        rows={1}
        className="flex-1 resize-none bg-bg border border-border rounded-lg px-3 py-2 text-base md:text-sm text-text placeholder-text-muted focus:outline-none focus:border-accent min-h-[40px]"
      />
      <button
        type="button"
        onClick={submit}
        disabled={!draft.trim()}
        className="px-3 min-h-[40px] rounded-lg bg-accent text-white text-sm font-medium disabled:opacity-40 hover:bg-accent/90 transition"
      >
        Send
      </button>
    </div>
  );
}

function XpEarnedTick({ amount }: { amount: number }) {
  return (
    <div className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-accent/10 border border-accent/25">
      <Zap size={15} strokeWidth={ICON_STROKE} className="text-accent" />
      <span className="text-sm font-semibold text-accent tabular-nums">
        +{amount} XP
      </span>
    </div>
  );
}

function RailCtas({
  isDaily,
  dailyChallenge,
  onNextSpot,
  onWalkThrough,
}: {
  isDaily: boolean;
  dailyChallenge?: DailyChallenge;
  onNextSpot: () => void;
  onWalkThrough: () => void;
}) {
  const primaryClass =
    "w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl bg-accent text-white text-sm font-medium hover:bg-accent/90 transition shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,0_8px_20px_-8px_rgba(124,147,241,0.6)]";
  const secondaryClass =
    "w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-border bg-surface text-sm font-medium text-text hover:border-accent hover:bg-accent/10 transition";

  // Daily mode advances the shared ladder via a fresh frame (remount on the
  // new query string) instead of "Next spot" (which would re-serve the
  // pinned spot).
  const next = dailyChallenge ? nextIncompleteDifficulty(dailyChallenge) : null;

  return (
    <div className="space-y-2">
      <button type="button" onClick={onWalkThrough} className={secondaryClass}>
        Walk through the reasoning
      </button>
      {isDaily ? (
        next && dailyChallenge ? (
          <Link
            to={dailyChallengeRoute({
              date: dailyChallenge.date,
              difficulty: next as DrillDifficulty,
              drillTarget: dailyChallenge.drillTarget,
              skillName: dailyChallenge.skillName,
            })}
            className={primaryClass}
          >
            Continue Today's Drill: {DIFFICULTY_LABEL[next]}
            <ChevronRight size={14} strokeWidth={ICON_STROKE} />
          </Link>
        ) : (
          <Link to="/drills" className={primaryClass}>
            Today's Drill complete — back to Drills
            <ChevronRight size={14} strokeWidth={ICON_STROKE} />
          </Link>
        )
      ) : (
        <button type="button" onClick={onNextSpot} className={primaryClass}>
          Next spot
          <ChevronRight size={14} strokeWidth={ICON_STROKE} />
        </button>
      )}
    </div>
  );
}

function LoadingPulse({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-6 text-text-muted">
      <LogoMark size={36} animate />
      <span className="text-sm animate-pulse">{label}</span>
    </div>
  );
}
